import React from 'react';
import { TextField } from '@mui/material';
import type { DigestPreferences } from '../../api/config';

/**
 * Account manager fields used in digest email footers. They live inside
 * `digest_preferences` server-side (no top-level columns) but are conceptually
 * a separate group from the per-section digest toggles, so they get their own
 * extracted component.
 *
 * Validation (e.g. AM email format) stays at the wrapper layer — the modal
 * surfaces errors via MUI's `error`/`helperText`; the dashboard does not yet
 * validate. The wrapper passes the error message through `emailError`.
 */
type AmKey =
  | 'account_manager_name'
  | 'account_manager_email'
  | 'account_manager_title'
  | 'account_manager_phone';

interface AccountManagerFieldsProps {
  value: Partial<DigestPreferences> | undefined;
  onChange: (patch: { digest_preferences: DigestPreferences }) => void;
  density?: 'standard' | 'compact';
  emailError?: string;
  onEmailFieldChange?: () => void;
}

// Server-side defaults from Location#default_digest_preferences. Used to
// promote a partial digest_preferences payload to a full DigestPreferences
// so consumer state types (CreateLocationData) stay sound.
const DEFAULT_PREFS: DigestPreferences = {
  restock: true,
  daily_usage: false,
  oos: false,
  rma: false,
};

export const AccountManagerFields: React.FC<AccountManagerFieldsProps> = ({
  value,
  onChange,
  density = 'standard',
  emailError,
  onEmailFieldChange,
}) => {
  const prefs = value ?? {};
  const fullWidth = density === 'standard';
  // The modal uses `size="small"` for AM fields even at standard density —
  // they're secondary metadata, not primary inputs. The dashboard's inline
  // editor matches.
  const size: 'small' = 'small';

  const update = (key: AmKey, val: string) => {
    onChange({ digest_preferences: { ...DEFAULT_PREFS, ...prefs, [key]: val } });
  };

  return (
    <>
      <TextField
        label="Name"
        value={prefs.account_manager_name ?? ''}
        onChange={e => update('account_manager_name', e.target.value)}
        fullWidth={fullWidth}
        size={size}
        placeholder="Server default"
      />
      <TextField
        label="Email"
        type="email"
        value={prefs.account_manager_email ?? ''}
        onChange={e => {
          update('account_manager_email', e.target.value);
          if (emailError && onEmailFieldChange) onEmailFieldChange();
        }}
        fullWidth={fullWidth}
        size={size}
        placeholder="Server default"
        error={!!emailError}
        helperText={emailError}
      />
      <TextField
        label="Title"
        value={prefs.account_manager_title ?? ''}
        onChange={e => update('account_manager_title', e.target.value)}
        fullWidth={fullWidth}
        size={size}
        placeholder="Server default"
      />
      <TextField
        label="Phone"
        value={prefs.account_manager_phone ?? ''}
        onChange={e => update('account_manager_phone', e.target.value)}
        fullWidth={fullWidth}
        size={size}
        placeholder="Server default"
      />
    </>
  );
};
